/**
 * Clientes, licenças e o que cada cliente possui.
 *
 * **Este módulo não existe no Sistema antigo.** Hoje a única porta para ver a
 * licença de um cliente é o PainelAdm, e é isso que estas telas trazem para cá.
 * O recorte é a ficha de **posse** — não o cadastro completo de cliente, que
 * continua no Painel.
 *
 * **Volume medido**: 152.609 clientes, dos quais **5.929 têm contrato vigente**.
 * Quem tem, tem no máximo 6 contratos (média 1,56) e até 68 produtos — por isso
 * a ficha não pagina nada e a busca pagina tudo.
 *
 * **Cinco palavras que parecem sinônimas e não são**, e a tela precisa acertar:
 *
 *   produto          a unidade do catálogo
 *   cliente_produto  posse **perpétua**, sem validade, em três canais
 *   pacotes          o item vendável
 *   licença          o **plano** — dias, visualizações, alcance
 *   contrato         a **assinatura concreta**; é o que a tela chama de "licença dele"
 *
 * **Tudo aqui é dado global**: não pertence a projeto de pinout, então a trava
 * recusa gravação em desenvolvimento e as telas avisam antes de deixar clicar.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, foraDoProjeto, obter } from "./cliente"

// ------------------------------------------------------------------ tipos

export type ClienteDaBusca = {
  id: number
  nome: string
  razaoSocial: string
  cpfCnpj: string
  /** "CPF", "CNPJ", "Irregular" ou vazio — sai da contagem de dígitos. */
  documento: string
  email: string
  localizacao: string
  cadastro: string | null
  licencasVigentes: number
  bloqueado: boolean
}

export type PaginaDeClientes = {
  itens: ClienteDaBusca[]
  total: number
  pagina: number
  porPagina: number
}

export type ContratoDoCliente = {
  id: number
  licencaId: number
  licenca: string
  classificacao: string
  data: string | null
  validade: string | null
  corteDeAtualizacao: string | null
  diasParaVencer: number
  situacao: number
  situacaoNome: string
  /** "Venda" quando veio de orçamento, "Cortesia" quando não. */
  origem: string
  orcamentoId: number
  painelId: number
  operador: string
  valor: number
  visualizacoesUsadas: number
  visualizacoesDoPlano: number
  semLimite: boolean
  conteudo: boolean
  sistema: boolean
  diagIA: boolean
  teste: boolean
  oficinaConectada: boolean
  vigente: boolean
  /** A regra real de `PermiteAcesso`, não "vence em tal data". */
  daAcesso: boolean
  /** Vazio quando dá acesso; o motivo, quando não dá. */
  porQueNao: string
}

export type ProdutoDoCliente = {
  produtoId: number
  produto: string
  origem: string
  data: string | null
  diagweb: boolean
  diag: boolean
  impresso: boolean
}

export type FichaDoCliente = {
  id: number
  nome: string
  razaoSocial: string
  cpfCnpj: string
  documento: string
  tipoCliente: string
  contato: string
  localizacao: string
  cadastro: string | null
  confirmado: boolean
  aceitouTermo: boolean
  oficinaConectada: boolean
  parceiro: boolean
  emails: { id: number; titulo: string; email: string; ativo: boolean; confirmado: boolean; principal: boolean }[]
  bloqueio: {
    manual: boolean
    manualAte: string | null
    manualVencido: boolean
    orcamentosBloqueados: number
    codigosDeOrcamento: number[]
  }
  contratos: ContratoDoCliente[]
  produtos: {
    total: number
    noDiagweb: number
    noDiag: number
    impressos: number
    itens: ProdutoDoCliente[]
  }
  resumo: {
    temAcessoAgora: boolean
    contratosVigentes: number
    contratosQueDaoAcesso: number
    venceEm: string | null
    situacao: string
  }
}

export type Licenca = {
  id: number
  titulo: string
  classificacao: string
  validadeEmDias: number
  validadeTexto: string
  visualizacoes: number
  semLimite: boolean
  atualizacaoEmDias: number
  corteIgualAValidade: boolean
  conteudo: boolean
  sistema: boolean
  diagIA: boolean
  teste: boolean
  categoriasDeVeiculo: { id: number; nome: string }[]
  tiposDeInformacao: { id: number; nome: string }[]
  contratosVigentes: number
}

export type SalvarLicenca = {
  titulo: string
  classificacao?: string
  validade: number
  visualizacoes: number
  atualizacao: number
  conteudo: boolean
  sistema: boolean
  diagIA: boolean
  teste: boolean
  categoriaVeiculoId: number
  categoriasDeVeiculo: number[]
  tiposDeInformacao: number[]
}

export type ResultadoDaOperacao = {
  operacao: string
  resultado: string
  contratoId: number | null
}

// ------------------------------------------------------------------ produtos

export type PacoteParaConceder = {
  id: number
  titulo: string
  ativo: boolean
  /** Zero em 16 dos 26: os pacotes de licença e de curso não ligam produto nenhum. */
  produtos: number
}

export type ItemDaConcessao = {
  posseId: number
  produtoId: number
  produto: string
  canal: Canal
  /** `entra` cria a posse, `ganha-canal` liga o canal numa que existe, `ja-tem` não faz nada. */
  efeito: "entra" | "ganha-canal" | "ja-tem"
  origemAtual: string
}

export type ConferenciaDaConcessao = {
  pacotes: number
  pacotesVazios: number
  produtos: number
  entram: number
  ganhamCanal: number
  jaTem: number
  /** Posses cuja origem deixa de dizer de onde vieram e passa a dizer MANUAL. */
  perdemProcedencia: number
  avisos: string[]
  itens: ItemDaConcessao[]
}

export type Canal = "diagweb" | "diag" | "impresso"

export const CANAIS: { chave: Canal; rotulo: string }[] = [
  { chave: "diagweb", rotulo: "Diagweb" },
  { chave: "diag", rotulo: "Diag" },
  { chave: "impresso", rotulo: "Impresso" },
]

export function usarPacotes(clienteId: number | null) {
  return useQuery({
    queryKey: ["pacotes-para-conceder"],
    enabled: clienteId != null,
    queryFn: () => obter<PacoteParaConceder[]>(`api/clientes/${clienteId}/produtos/pacotes`, {}),
    // O cadastro de pacotes muda uma vez por ano; não vale reconsultar a cada
    // cliente aberto. A rota é aninhada no cliente só por causa da chave de acesso.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })
}

/**
 * A conferência da concessão.
 *
 * **Só roda quando pedida.** É a mesma regra das outras operações perigosas: a
 * pessoa escolhe, confere, e só então o botão de aplicar aparece — com o número
 * do que vai mudar escrito nele.
 */
export function usarConferirConcessao(
  clienteId: number, pacotes: number[], canais: Canal[], pedido: number,
) {
  return useQuery({
    queryKey: ["conferir-concessao", clienteId, [...pacotes].sort().join(","), canais.join(","), pedido],
    enabled: pedido > 0 && pacotes.length > 0 && canais.length > 0,
    queryFn: () => obter<ConferenciaDaConcessao>(
      `api/clientes/${clienteId}/produtos/conferir`,
      { pacoteIds: pacotes, canais }),
    refetchOnWindowFocus: false,
    retry: false,
  })
}

// ------------------------------------------------------------------ busca

/**
 * A busca só sai do lugar com 2 caracteres — regra 2 da casa, e aqui ela tem
 * dente: com uma letra a consulta varreria 152.609 clientes e 165.242 e-mails
 * para devolver meio cadastro. A API também recusa, então nem chega ao banco.
 */
export function useBuscaDeClientes(
  busca: string,
  somenteComLicenca: boolean,
  pagina: number,
  porPagina = 50,
) {
  const termo = busca.trim()
  return useQuery({
    queryKey: ["clientes", termo, somenteComLicenca, pagina, porPagina],
    enabled: termo.length >= 2,
    queryFn: () =>
      obter<PaginaDeClientes>("api/clientes", {
        busca: termo,
        somenteComLicenca,
        pagina,
        porPagina,
      }),
    // Nada de refetch ao voltar para a janela: a lista é resultado de uma busca
    // que a pessoa fez, não um painel que se atualiza sozinho.
    refetchOnWindowFocus: false,
  })
}

export function useFichaDoCliente(clienteId: number | null) {
  return useQuery({
    queryKey: ["cliente", clienteId],
    enabled: clienteId != null,
    queryFn: () => obter<FichaDoCliente>(`api/clientes/${clienteId}`),
    refetchOnWindowFocus: false,
  })
}

// ------------------------------------------------------------------ licenças

export function useLicencas() {
  return useQuery({
    queryKey: ["licencas"],
    queryFn: () => obter<Licenca[]>("api/licencas"),
    refetchOnWindowFocus: false,
  })
}

export function useSalvarLicenca() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, dados }: { id: number | null; dados: SalvarLicenca }) => {
      const r = id == null
        ? await api.post<Licenca>("api/licencas", dados, foraDoProjeto())
        : await api.put<Licenca>(`api/licencas/${id}`, dados, foraDoProjeto())
      return r.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["licencas"] }),
  })
}

export function useExcluirLicenca() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`api/licencas/${id}`, foraDoProjeto())
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["licencas"] }),
  })
}

// ------------------------------------------------------------------ operações

/**
 * As seis operações que administram licença.
 *
 * A sexta — conceder produtos — é a única que o PainelAdm faz **sem deixar
 * rastro**: lá ela manda um e-mail para dois endereços escritos no código e não
 * escreve linha nenhuma no histórico do cliente.
 *
 * Todas invalidam a ficha do cliente ao terminar, porque todas mudam o que ela
 * mostra — e mostrar a ficha velha depois de prorrogar seria a tela mentindo
 * sobre o que acabou de acontecer.
 */
export function useOperacaoDeLicenca(clienteId: number) {
  const qc = useQueryClient()

  const invalidar = () => {
    qc.invalidateQueries({ queryKey: ["cliente", clienteId] })
    // A busca mostra a contagem de licenças vigentes por cliente.
    qc.invalidateQueries({ queryKey: ["clientes"] })
  }

  const conceder = useMutation({
    mutationFn: async (dados: { licencaId: number; dias?: number; motivo: string }) =>
      (await api.post<ResultadoDaOperacao>(
        `api/clientes/${clienteId}/licencas`, dados, foraDoProjeto())).data,
    onSuccess: invalidar,
  })

  const prorrogar = useMutation({
    mutationFn: async ({ contratoId, dias, motivo }: { contratoId: number; dias: number; motivo: string }) =>
      (await api.post<ResultadoDaOperacao>(
        `api/clientes/${clienteId}/contratos/${contratoId}/prorrogar`,
        { dias, motivo }, foraDoProjeto())).data,
    onSuccess: invalidar,
  })

  const corte = useMutation({
    mutationFn: async ({ contratoId, data, motivo }: { contratoId: number; data: string | null; motivo: string }) =>
      (await api.put<ResultadoDaOperacao>(
        `api/clientes/${clienteId}/contratos/${contratoId}/corte-de-atualizacao`,
        { data, motivo }, foraDoProjeto())).data,
    onSuccess: invalidar,
  })

  const bloquear = useMutation({
    mutationFn: async (motivo: string) =>
      (await api.post<ResultadoDaOperacao>(
        `api/clientes/${clienteId}/bloqueio`, { motivo }, foraDoProjeto())).data,
    onSuccess: invalidar,
  })

  const desbloquear = useMutation({
    mutationFn: async (motivo: string) =>
      (await api.delete<ResultadoDaOperacao>(
        `api/clientes/${clienteId}/bloqueio`, { ...foraDoProjeto(), data: { motivo } })).data,
    onSuccess: invalidar,
  })

  const devolverVisualizacoes = useMutation({
    mutationFn: async (motivo: string) =>
      (await api.post<ResultadoDaOperacao>(
        `api/clientes/${clienteId}/visualizacoes/devolver`, { motivo }, foraDoProjeto())).data,
    onSuccess: invalidar,
  })

  const concederProdutos = useMutation({
    mutationFn: async (dados: { pacoteIds: number[]; canais: Canal[]; motivo: string }) =>
      (await api.post<ResultadoDaOperacao>(
        `api/clientes/${clienteId}/produtos`, dados, foraDoProjeto())).data,
    onSuccess: invalidar,
  })

  return {
    conceder, prorrogar, corte, bloquear, desbloquear, devolverVisualizacoes, concederProdutos,
  }
}

// ------------------------------------------------------------------ formato

/** Data ISO para dd/mm/aaaa. Nulo vira travessão, e não "Invalid Date". */
export function data(iso: string | null | undefined): string {
  if (!iso) return "—"
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("pt-BR")
}

/**
 * A cota de visualizações como texto.
 *
 * `0` é o sentinela de **ilimitado** — é o que `PermiteAcesso` lê. As licenças
 * anuais gravam `1000000`, que é finito no papel e infinito na prática: mostrar
 * "50 de 1.000.000" não informa ninguém, então acima de um milhão a tela diz
 * que não há limite e conta só o usado.
 */
export function cota(usadas: number, total: number, semLimite: boolean): string {
  const n = (v: number) => v.toLocaleString("pt-BR")
  if (semLimite || total === 0) return `${n(usadas)} usadas · sem limite`
  if (total >= 1_000_000) return `${n(usadas)} usadas · sem limite prático`
  return `${n(usadas)} de ${n(total)}`
}
